import React, { useMemo, useState } from 'react';
import { App, Button, Modal, Space, Table, Tag, Tooltip, Upload } from 'antd';
import { DownloadOutlined, FileExcelOutlined, PictureOutlined, ReloadOutlined, UploadOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { usePermission } from 'components/PermissionProvider';
import { GET_WEIGHING_TASKS } from 'graphql/queries/weighing';
import { WeighingTask, PagedWeighingTask } from 'graphql/types';
import {
    downloadWeighingDetails,
    downloadWeighingTemplate,
    getWeighingImgDetails,
    PortalRestError,
    uploadWeighingTask,
    WeighingCreateResponse,
    WeighingImgDetail,
} from 'utils/portalRestClient';

const RUNNING_STATUSES = new Set(['PENDING', 'PROCESSING']);
const FINAL_STATUSES = new Set(['ALL_SUCCESS', 'PARTIAL_SUCCESS', 'ALL_FAILED']);

const statusColor: Record<string, string> = {
    PENDING: 'default',
    PROCESSING: 'processing',
    ALL_SUCCESS: 'success',
    PARTIAL_SUCCESS: 'warning',
    ALL_FAILED: 'error',
};

const progressColor: Record<string, string> = {
    executing: 'processing',
    completed: 'success',
};

type WeighingTaskRow = WeighingTask & {
    local?: boolean;
};

const formatDate = (value?: string | null): string => {
    return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '-';
};

const formatBytes = (value?: string | null): string => {
    const size = Number(value || 0);
    if (!size) return '-';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const getProgressKey = (task: WeighingTask): string => {
    if (RUNNING_STATUSES.has(task.status)) return 'executing';
    if (FINAL_STATUSES.has(task.status)) return 'completed';
    return 'executing';
};

const WeighingQueryPage: React.FC = () => {
    const { t } = useTranslation();
    const { message } = App.useApp();
    const { hasPermission } = usePermission();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [localTasks, setLocalTasks] = useState<WeighingTaskRow[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [imgModalTask, setImgModalTask] = useState<WeighingTask | null>(null);
    const [imgDetails, setImgDetails] = useState<WeighingImgDetail[]>([]);
    const [imgDetailsLoading, setImgDetailsLoading] = useState(false);

    const { data, loading, refetch } = useQuery<{ portalWeighingTasks: PagedWeighingTask }>(
        GET_WEIGHING_TASKS,
        {
            variables: { page: page - 1, size: pageSize },
            pollInterval: 0,
            fetchPolicy: 'cache-and-network',
        },
    );

    const hasRunningTask = useMemo(
        () => (data?.portalWeighingTasks?.content || []).some((task) => RUNNING_STATUSES.has(task.status)),
        [data],
    );

    const taskRows = useMemo<WeighingTaskRow[]>(
        () => [...localTasks, ...(data?.portalWeighingTasks?.content || [])],
        [localTasks, data],
    );

    React.useEffect(() => {
        if (!hasRunningTask) return;
        const timer = window.setInterval(() => refetch(), 5000);
        return () => window.clearInterval(timer);
    }, [hasRunningTask, refetch]);

    const renderStatus = (status: string) => (
        <Tag color={statusColor[status] || 'default'}>
            {t(`weighingQuery.status.${status}`, status)}
        </Tag>
    );

    const renderProgress = (task: WeighingTask) => {
        const key = getProgressKey(task);
        return (
            <Tag color={progressColor[key] || 'default'}>
                {t(`weighingQuery.progress.${key}`)}
            </Tag>
        );
    };

    const handleUpload = async () => {
        if (!selectedFile || uploading) return;
        const file = selectedFile;
        const localId = `local-${Date.now()}`;
        setLocalTasks((prev) => [{
            id: localId,
            sourceFileName: file.name,
            sourceFileSize: String(file.size),
            status: 'PENDING',
            totalCount: 0,
            successCount: 0,
            failedCount: 0,
            ignoredCount: 0,
            createdAccountNumber: null,
            createdAt: dayjs().toISOString(),
            updatedAt: null,
            completedAt: null,
            local: true,
        }, ...prev]);
        setUploading(true);
        try {
            const result: WeighingCreateResponse = await uploadWeighingTask(file);
            setLocalTasks((prev) => prev.filter((task) => task.id !== localId));
            message.success(t('weighingQuery.messages.uploadSuccess'));
            setSelectedFile(null);
            await refetch();
            void result;
        } catch (error) {
            if (error instanceof PortalRestError) {
                setLocalTasks((prev) => prev.filter((task) => task.id !== localId));
            } else {
                setLocalTasks((prev) => prev.filter((task) => task.id !== localId));
            }
            // uploadWeighingTask already shows error message from the server response.
        } finally {
            setUploading(false);
        }
    };

    const handleTemplateDownload = async () => {
        if (templateLoading) return;
        setTemplateLoading(true);
        try {
            await downloadWeighingTemplate();
        } finally {
            setTemplateLoading(false);
        }
    };

    const handleDetailsDownload = async (task: WeighingTask) => {
        await downloadWeighingDetails(task.id);
    };

    const handleImgDetailsClick = async (task: WeighingTask) => {
        setImgModalTask(task);
        setImgDetails([]);
        setImgDetailsLoading(true);
        try {
            const result = await getWeighingImgDetails(task.id);
            setImgDetails(result);
        } catch {
            // error already shown by portalRestClient
        } finally {
            setImgDetailsLoading(false);
        }
    };

    const taskColumns = [
        {
            title: 'taskId',
            dataIndex: 'id',
            key: 'id',
            width: 100,
        },
        {
            title: t('weighingQuery.task.status'),
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: renderStatus,
        },
        {
            title: t('weighingQuery.task.sourceFile'),
            dataIndex: 'sourceFileName',
            key: 'sourceFileName',
            width: 360,
            render: (value: string | null, record: WeighingTask) => {
                const fileName = value || '-';
                return (
                    <div className="flex min-w-0 items-center gap-2">
                        <FileExcelOutlined className="shrink-0" />
                        <Tooltip title={fileName}>
                            <span className="min-w-0 flex-1 truncate">{fileName}</span>
                        </Tooltip>
                        <span className="shrink-0 text-gray-400">{formatBytes(record.sourceFileSize)}</span>
                    </div>
                );
            },
        },
        {
            title: t('weighingQuery.task.progress'),
            key: 'progress',
            width: 120,
            render: (_: unknown, record: WeighingTaskRow) => renderProgress(record),
        },
        {
            title: t('weighingQuery.task.total'),
            dataIndex: 'totalCount',
            key: 'totalCount',
            width: 90,
        },
        {
            title: t('weighingQuery.task.success'),
            dataIndex: 'successCount',
            key: 'successCount',
            width: 90,
        },
        {
            title: t('weighingQuery.task.failed'),
            dataIndex: 'failedCount',
            key: 'failedCount',
            width: 90,
        },
        {
            title: t('weighingQuery.task.ignored'),
            dataIndex: 'ignoredCount',
            key: 'ignoredCount',
            width: 90,
        },
        {
            title: t('weighingQuery.task.createdBy'),
            dataIndex: 'createdAccountNumber',
            key: 'createdAccountNumber',
            width: 130,
            render: (value: string | null) => value || '-',
        },
        {
            title: t('weighingQuery.task.createdAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            render: formatDate,
        },
        {
            title: t('weighingQuery.task.completedAt'),
            dataIndex: 'completedAt',
            key: 'completedAt',
            width: 170,
            render: formatDate,
        },
        {
            title: t('weighingQuery.task.actions'),
            key: 'actions',
            width: 140,
            render: (_: unknown, record: WeighingTaskRow) => record.local ? '-' : (
                FINAL_STATUSES.has(record.status) ? (
                    <Space>
                        <Tooltip title={t('weighingQuery.actions.download')}>
                            <Button
                                type="text"
                                icon={<DownloadOutlined />}
                                onClick={() => handleDetailsDownload(record)}
                            />
                        </Tooltip>
                        <Tooltip title={t('weighingQuery.actions.fileDownload')}>
                            <Button
                                type="text"
                                icon={<PictureOutlined />}
                                onClick={() => handleImgDetailsClick(record)}
                            />
                        </Tooltip>
                    </Space>
                ) : '-'
            ),
        },
    ];

    const imgDetailColumns = [
        {
            title: t('weighingQuery.imgDetails.batchSeq'),
            key: 'seq',
            width: 60,
            render: (_: unknown, __: WeighingImgDetail, index: number) => index + 1,
        },
        {
            title: t('weighingQuery.imgDetails.firstTrackingNumber'),
            dataIndex: 'firstTrackingNumber',
            key: 'firstTrackingNumber',
        },
        {
            title: t('weighingQuery.imgDetails.compressedFileSize'),
            dataIndex: 'compressedFileSize',
            key: 'compressedFileSize',
            width: 120,
            render: (v: number | null) => formatBytes(v != null ? String(v) : null),
        },
        {
            title: t('weighingQuery.imgDetails.uploadCompletedAt'),
            dataIndex: 'uploadCompletedAt',
            key: 'uploadCompletedAt',
            width: 180,
            render: (v: string | null) => v || '-',
        },
        {
            title: t('weighingQuery.imgDetails.downloadUrl'),
            dataIndex: 'downloadUrl',
            key: 'downloadUrl',
            render: (url: string | null) => url
                ? (
                    <Button
                        type="primary"
                        size="small"
                        icon={<DownloadOutlined />}
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {t('weighingQuery.imgDetails.download')}
                    </Button>
                )
                : '-',
        },
    ];

    return (
        <div className="bg-white rounded-lg p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <Space wrap>
                    {hasPermission('portal:weighing-query:upload') && (
                        <>
                            <Upload
                                accept=".xlsx,.xls"
                                maxCount={1}
                                beforeUpload={(file) => {
                                    setSelectedFile(file);
                                    return false;
                                }}
                                onRemove={() => setSelectedFile(null)}
                                fileList={selectedFile ? [{
                                    uid: selectedFile.name,
                                    name: selectedFile.name,
                                    status: 'done',
                                }] : []}
                            >
                                <Button icon={<FileExcelOutlined />}>{t('weighingQuery.actions.selectFile')}</Button>
                            </Upload>
                            <Button
                                type="primary"
                                icon={<UploadOutlined />}
                                disabled={!selectedFile}
                                loading={uploading}
                                onClick={handleUpload}
                            >
                                {t('weighingQuery.actions.upload')}
                            </Button>
                        </>
                    )}
                    {hasPermission('portal:weighing-query:template') && (
                        <Button
                            icon={<DownloadOutlined />}
                            loading={templateLoading}
                            onClick={handleTemplateDownload}
                        >
                            {t('weighingQuery.actions.template')}
                        </Button>
                    )}
                </Space>
                <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                    {t('weighingQuery.actions.refresh')}
                </Button>
            </div>

            <Table<WeighingTaskRow>
                columns={taskColumns}
                dataSource={taskRows}
                loading={loading}
                rowKey="id"
                scroll={{ x: 1570 }}
                pagination={{
                    current: page,
                    pageSize,
                    total: data?.portalWeighingTasks?.totalElements || 0,
                    onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                    showSizeChanger: true,
                    showTotal: (total) => t('weighingQuery.pagination.total', { total }),
                }}
            />
            <Modal
                title={t('weighingQuery.imgDetails.title')}
                open={imgModalTask !== null}
                onCancel={() => setImgModalTask(null)}
                footer={null}
                width={800}
            >
                <Table<WeighingImgDetail>
                    columns={imgDetailColumns}
                    dataSource={imgDetails}
                    loading={imgDetailsLoading}
                    rowKey="id"
                    pagination={false}
                    locale={{ emptyText: t('weighingQuery.imgDetails.noData') }}
                />
            </Modal>
        </div>
    );
};

export default WeighingQueryPage;
